import React, { forwardRef, useEffect, useState } from 'react';
import classNames from 'classnames';
import { SVG_ICON_HASH } from './svgIconHash';

export interface IIcontFont extends React.HTMLAttributes<HTMLDivElement> {
  name: string;
  fontSize?: string | number;
  color?: string;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
}

/** 线上 SVG 公共读域名（与 OSS Bucket 对应，写死） */
const SVG_OSS_PUBLIC_ORIGIN = 'https://roboneo-public.meitudata.com';

/** 已解析的 SVG 内联 HTML，按请求 URL 缓存，避免重复请求 */
const iconSvgCache = new Map<string, string>();
/** 进行中的 fetch，相同 URL 复用同一 Promise */
const pendingFetches = new Map<string, Promise<string>>();

/** 线上 SVG 并发上限，兼顾首屏图标尽快呈现与不过度抢占连接 */
const SVG_FETCH_MAX_CONCURRENT = 6;

/** 等待执行的 SVG 拉取任务 */
const svgFetchQueue: Array<() => void> = [];

/** 当前进行中的 SVG 网络请求数 */
let activeSvgFetches = 0;

/**
 * 是否为本地开发环境（Next 等使用 NODE_ENV=development）。
 * 仅在模块首次加载时计算一次，后续复用，避免重复读取 process。
 */
const IS_LOCAL_DEV = typeof process !== 'undefined' && process.env.NODE_ENV === 'development';

/**
 * 拼接当前环境下该图标的 SVG 请求 URL（name 与文件名一致，不做大小写转换）。
 * @param {string} name Icon 名称，与旧版一致
 * @returns {string}
 */
function buildSvgUrl(name: string): string {
  const file = `${name}.svg`;
  if (IS_LOCAL_DEV) {
    return `/svg/${file}`;
  }
  const origin = SVG_OSS_PUBLIC_ORIGIN.replace(/\/$/, '');
  return `${origin}/public/roboneo_web/svgicon/${SVG_ICON_HASH}/${file}`;
}

/**
 * 将远端 SVG 文本转为可内联的 HTML（尺寸、着色、裁剪 defs 等与参考实现一致）。
 * @param {string} svgText
 * @returns {string}
 */
function transformSvgMarkup(svgText: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, 'image/svg+xml');
  const svg = doc.querySelector('svg');
  if (!svg) {
    throw new Error('Invalid SVG: missing root <svg>');
  }
  svg.setAttribute('width', '1em');
  svg.setAttribute('height', '1em');
  svg.querySelectorAll('[fill]').forEach((el) => {
    const fill = el.getAttribute('fill');
    if (fill && fill.toLowerCase() !== 'none') {
      el.setAttribute('fill', 'currentColor');
    }
  });
  svg.querySelectorAll('defs').forEach((defs) => defs.remove());
  ['clip-path', 'mask', 'filter'].forEach((attr) => {
    svg.querySelectorAll(`[${attr}]`).forEach((el) => {
      el.removeAttribute(attr);
    });
  });
  return svg.outerHTML;
}

/**
 * 从队列中取出下一项 SVG 拉取任务。
 */
function drainSvgFetchQueue(): void {
  while (activeSvgFetches < SVG_FETCH_MAX_CONCURRENT && svgFetchQueue.length > 0) {
    const run = svgFetchQueue.shift();
    run?.();
  }
}

/**
 * 在并发限制内立即执行 SVG 网络请求（高优先级 fetch，不再等待页面 load）。
 * @param {string} url
 * @returns {Promise<string>}
 */
function fetchSvgWithScheduler(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const run = () => {
      activeSvgFetches += 1;
      fetch(url, { priority: 'auto' } as RequestInit)
        .then(async (res) => {
          if (!res.ok) {
            throw new Error(`Failed to load SVG: ${url} (${res.status})`);
          }
          const text = await res.text();
          resolve(transformSvgMarkup(text));
        })
        .catch(reject)
        .finally(() => {
          activeSvgFetches -= 1;
          drainSvgFetchQueue();
        });
    };

    if (activeSvgFetches < SVG_FETCH_MAX_CONCURRENT) {
      run();
    } else {
      svgFetchQueue.push(run);
    }
  });
}

/**
 * 拉取并解析 SVG；同一 URL 全局只发起一次网络请求。
 * @param {string} url
 * @returns {Promise<string>}
 */
async function loadSvgOnce(url: string): Promise<string> {
  if (iconSvgCache.has(url)) {
    return iconSvgCache.get(url)!;
  }
  const existing = pendingFetches.get(url);
  if (existing) {
    return existing;
  }

  const promise = fetchSvgWithScheduler(url)
    .then((html) => {
      iconSvgCache.set(url, html);
      return html;
    })
    .finally(() => {
      pendingFetches.delete(url);
    });

  pendingFetches.set(url, promise);
  return promise;
}

const IconFont = forwardRef<HTMLDivElement, IIcontFont>((props, ref) => {
  const {
    className = '',
    name,
    color,
    fontSize = 12,
    style: styleProp = {},
    onClick,
    ...restProps
  } = props;

  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!name) {
      return undefined;
    }

    let cancelled = false;
    const url = buildSvgUrl(name);

    (async () => {
      try {
        const html = await loadSvgOnce(url);
        if (!cancelled) {
          setSvgContent(html);
          setError(false);
        }
      } catch (e) {
        console.error(`[IconFont] load error [${name}]:`, e);
        if (!cancelled) {
          setError(true);
          setSvgContent(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [name]);

  const size = styleProp.fontSize ?? fontSize;

  return (
    <div
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...restProps}
      className={classNames('roboneo-iconfont', `icon-${name}`, className)}
      style={{
        width: size,
        height: size,
        color,
        fontSize: size,
        ...styleProp,
      }}
      // SVG 已由本逻辑解析为受控内联片段
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={svgContent && !error ? { __html: svgContent } : undefined}
      ref={ref}
      onClick={onClick}
    />
  );
});

IconFont.displayName = 'IconFont';

export default React.memo(IconFont);
